import React, { useEffect, useState } from 'react'
import { ViewProps } from '../calendar/types'
import style from './WeekView.module.css'
import dayjs from 'dayjs'
import { dateToDateString, getMonday } from '../../common/utils/date'
import { Event as E } from '../../modules/calendar/models/Event'
import { Query, QueryParam } from '../../common/api/Query'
import { Box, Button, Modal, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { getUserContainer } from '../../container/user-module'
import { capitalize, reduceString } from '../../common/utils/strings'
import { CalendarModal } from '../calendar/CalendarModal'
import { CalendarModalMultiEvents } from '../calendar/CalendarModalMultiEvents'
import { ICircleService } from '../../modules/users/services/CircleService'
import { CIRCLE_SERVICE_KEY, LOGGED_USER_SERVICE_KEY } from '../../modules/users/container'
import { getCalendarContainer } from '../../container/calendar-module'
import { EventService } from '../../modules/calendar/services/EventService'
import { EVENT_SERVICE_KEY } from '../../modules/calendar'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { useTranslation } from 'react-i18next'
import addIcon from '../../assets/calendar/add.svg'
import { ROUTE_CALENDAR_FORM, ROUTE_CREATE } from '../../routes/routes-constants'
import genericStyle from '../../common/utils/generic.module.css'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { EventCategory } from '../../modules/calendar/enums/EventCategory'
import { Tooltip } from '@mui/material'
import reviewIcon from '../../assets/events_icons/bola-revision.svg'
import dateIcon from '../../assets/calendar/cita.svg'
import vaccineIcon from '../../assets/events_icons/bola-vacunas.svg'
import adviceIcon from '../../assets/events_icons/bola-consejos.svg'
import landmarkIcon from '../../assets/events_icons/bola-hitos.svg'
import trainingIcon from '../../assets/calendar/ejercicio.svg'
import formIcon from '../../assets/events_icons/evento-formulario.svg'
import othersIcon from '../../assets/events_icons/bola-otros.svg'
import medicationIcon from '../../assets/dashboard/treatmentIconEvent.svg'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCircleContext } from '../../common/context/CircleContext'

const calendarContainer = getCalendarContainer()
const eventService = calendarContainer.get<EventService>(EVENT_SERVICE_KEY)

const hours = (): string[] => {
  const hourRange = []
  for (let i = 7; i < 23; i++) {
    if (i < 10) {
      hourRange.push('0' + i + ':00')
    } else {
      hourRange.push(i + ':00')
    }
  }
  return hourRange
}

type WeekViewProps = { id: string | undefined } & ViewProps

type WeekEvent = {
  event: E
}
const loggedUserService = getUserContainer().get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)
export function WeekViewHome(props: WeekViewProps) {
  const { t } = useTranslation()
  const [selectedEvent, setSelectedEvent] = useState<E>()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [dateTimeMultiEvents, setDateTimeMultiEvents] = useState<{
    date: Date | string
    hour: string
  }>()
  const [openModalMultiEvent, setOpenModalMultiEvent] = useState<boolean>(false)
  const [events, setEvents] = useState<E[]>([])
  const [monday, setMonday] = useState<Date>(getMonday(props.selectedDate))
  const [date, setDate] = useState<Date>(props.selectedDate || new Date())
  const loggedUser = loggedUserService.get()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { circle } = useCircleContext()

  useEffect(() => {
    const finishDate = new Date(monday)
    finishDate.setDate(finishDate.getDate() + 7)

    const userId = circle?.patient?.id || loggedUser?.id || ''

    eventService
      .getFilteredList(
        new Query({
          query: [
            new QueryParam<E>('startDate', monday.toISOString()),
            new QueryParam<E>('finishDate', finishDate.toISOString()),
          ],
        }),
        userId
      )
      .subscribe(
        (res) => {
          setEvents([...res.items])
        },
        (error) => {
          console.error('WeekViewHome - Error fetching events:', error) // Log para capturar errores
        }
      )
  }, [monday, circle]) // Añadir "circle" como dependencia

  useEffect(() => {
    const tmpMonday = getMonday(props.selectedDate)
    tmpMonday.getTime() !== monday.getTime() && setMonday(tmpMonday)
  }, [props.selectedDate])

  const handleCloseModal = () => setOpenModal(false)

  const editEvent = (e: E) => {
    navigate(`${ROUTE_CALENDAR_FORM}/${e.id}`, { state: { date: date } })
    setOpenModal(false)
  }

  const deleteEvent = (e: E) => {
    if (e.id) {
      eventService.delete(e.id).subscribe(() => {
        setDate(new Date(date))
        setIsLoading(!isLoading)
      })
    }
    setOpenModal(false)
  }

  const getIcon = (type: EventCategory) => {
    switch (type) {
      case EventCategory.Reviews:
        return <img src={reviewIcon} alt={'reviews'} />

      case EventCategory.Medication:
        return <img src={medicationIcon} alt={'treatment'} />

      case EventCategory.Vaccines:
        return <img src={vaccineIcon} alt={'vaccine'} />

      case EventCategory.Appointment:
        return <img src={dateIcon} alt={'medicalappointment'} />

      case EventCategory.Advices:
        return <img src={adviceIcon} alt={'advices'} />

      case EventCategory.Landmarks:
        return <img src={landmarkIcon} alt={'landmarks'} />

      case EventCategory.Training:
        return <img src={trainingIcon} alt={'training'} />

      case EventCategory.Forms:
        return <img src={formIcon} alt={'training'} />

      default:
        return <img src={othersIcon} alt={'others'} />
    }
  }

  const getEventTypeName = (type: EventCategory) => {
    switch (type) {
      case EventCategory.Reviews:
        return t('reviews')

      case EventCategory.Medication:
        return t('treatment')

      case EventCategory.Vaccines:
        return t('vaccine')

      case EventCategory.Appointment:
        return t('medicalappointment')

      case EventCategory.Advices:
        return t('advices')

      case EventCategory.Landmarks:
        return t('landmarks')

      case EventCategory.Training:
        return t('training')

      case EventCategory.Forms:
        return t('training')

      default:
        return t('others')
    }
  }

  const getColorByCategory = (category: number) => {
    let color

    switch (category) {
      case 1:
        color = '#2E7161'
        break

      case 2:
        color = '#f37e31'
        break

      case 3:
        color = '#d2617e'
        break

      case 4:
        color = '#eadf33'
        break

      case 5:
        color = '#3fbfcc'
        break

      case 6:
        color = '#2E7161'
        break

      case 7:
        color = '#815abb'
        break

      case 8:
        color = '#9b8c58'
        break
      default:
        color = '#959595'
        break
    }
    return color
  }

  const handlerClick = (event: E) => {
    setSelectedEvent(event)
    setOpenModal(true)
  }

  function WeekEvent(props: WeekEvent): JSX.Element {
    let duration
    if (
      dayjs(props.event.finishDate).format('DD').toString() ===
      dayjs(props.event.startDate).format('DD').toString()
    ) {
      duration =
        Number(dayjs(props.event.finishDate).format('HH')) -
        Number(dayjs(props.event.startDate).format('HH'))
    }

    const backgroundColor = getColorByCategory(props.event.eventCategory)
    const textColor = backgroundColor === '#2E7161' ? 'white' : 'black'

    return (
      <>
        {props.event.startDate.getHours() === props.event.finishDate.getHours() ? (
          <Tooltip
            title={`${getEventTypeName(props.event.eventCategory)}: ${reduceString(
              props.event.title,
              15
            )} (${dayjs(props.event.startDate).format('HH:mm')})`}
          >
            <Box
              className={style.eventNotSameDay}
              onClick={() => handlerClick(props.event)}
              style={{
                backgroundColor: backgroundColor,
                color: textColor,
              }}
            >
              <div className={style.iconEventCategoryNotSameDay}>
                {getIcon(props.event.eventCategory)}
              </div>
              <h3 className={style.eventTitleNotSameDay} style={{ color: textColor }}>
                {reduceString(props.event.title, 15)}
              </h3>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={`${getEventTypeName(props.event.eventCategory)}: ${reduceString(
              props.event.title,
              15
            )} (${dayjs(props.event.startDate).format('HH:mm')})`}
          >
            <Box
              className={style.event}
              onClick={() => handlerClick(props.event)}
              style={{
                ['--duration-height' as any]: duration,
                ['--col' as any]: backgroundColor,
                backgroundColor: backgroundColor,
                color: textColor,
              }}
            >
              {getIcon(props.event.eventCategory)}
              <h3 className={style.eventTitle} style={{ color: textColor }}>
                {reduceString(props.event.title, 15)}
              </h3>
            </Box>
          </Tooltip>
        )}
      </>
    )
  }

  const weekDays = (): Date[] => {
    let datesRange = [monday]

    for (let i = 1; i < 7; i++) {
      let newDate = new Date(monday)
      newDate.setDate(monday.getDate() + i)
      datesRange.push(newDate)
    }
    return datesRange
  }

  const createEvent = () =>
    navigate(`${ROUTE_CALENDAR_FORM}/${ROUTE_CREATE}`, {
      state: { date: date, currentUrl: location.pathname },
    })

  const patientName = circle?.patient
    ? `${circle.patient.firstName} ${circle.patient.lastName}`
    : ''

  const handleOpenModalMultiEvent = (d: string, h: string) => {
    setDateTimeMultiEvents({ date: d, hour: h })
    setOpenModalMultiEvent(true)
  }

  const handleCloseModalMultiEvent = () => setOpenModalMultiEvent(false)

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginLeft: openMenuLeft ? '50px' : '',
      }}
    >
      <div
        style={{
          maxWidth: openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)',
        }}
      >
        {patientName && (
          <h3 style={{ marginBottom: '20px', color: '#a9ba37', fontWeight: 'bold' }}>
            {t('resident')}: {patientName}
          </h3>
        )}
        <div className={style.container}>
          <Table
            sx={{
              borderRadius: '27px',
              border: 'none',
              background: 'transparent',
              width: 'auto',
              '& td': {
                borderStyle: 'dotted',
                borderColor: '#2E7161',
                '&:nth-child(odd)': {
                  backgroundColor: 'rgba(173,184,78,0.06)',
                },
                '&:last-child': {
                  borderRightWidth: 1,
                  borderRightStyle: 'dotted',
                  borderColor: '#2E7161',
                },
                '&:first-child': {
                  borderLeftWidth: 1,
                  borderLeftStyle: 'dotted',
                  borderColor: '#2E7161',
                },
              },
              '& tr': {
                borderStyle: 'dotted',
                borderColor: '#2E7161',
                '&:nth-child(odd)': {
                  backgroundColor: 'rgba(173,184,78,0.06)',
                },
              },
              '& th': {
                borderBottomStyle: 'dotted',
                borderColor: '#2E7161',
                padding: '14px 0 5px 0',
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: openMenuLeft ? '' : '100%',
                marginLeft: openMenuLeft ? 30 : '',
                marginRight: openMenuLeft ? 30 : '',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className={style.weekHeader}
                    style={{
                      visibility: 'hidden',
                      width: '37px',
                      padding: '0',
                      margin: '0',
                    }}
                  >
                    <div className={style.hour} style={{ marginRight: 18 }}></div>
                  </TableCell>
                  {weekDays().map((d, i) => (
                    <TableCell className={style.weekHeader}>
                      <div className={`${i === 0 ? style.day1 : ''}`} key={dateToDateString(d)}>
                        <div
                          className={
                            new Date(d).getDay() === new Date().getDay()
                              ? style.weekNoDay
                              : style.weekDay
                          }
                        >
                          <div className={style.weekDayNumber}>{dayjs(d).format('DD')}</div>
                          <div className={style.weekDayName}>
                            {window.innerWidth > 480
                              ? capitalize(dayjs(d).locale(navigator.language).format('dddd'))
                              : capitalize(dayjs(d).locale(navigator.language).format('dd'))}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {hours().map((h, i) => (
                  <TableRow>
                    <TableCell key={i} className={style.hourRow}>
                      <div style={{ maxHeight: '42px' }} className={style.hour} key={i}>
                        {h}
                      </div>
                    </TableCell>
                    {weekDays().map((d) => (
                      <TableCell className={style.eventRow} key={String(d) + String(h)}>
                        <div className={style.containerEvent}>
                          {events.map((event: E) =>
                            dayjs(d).format('DD').toString() ===
                              dayjs(event.startDate).format('DD').toString() &&
                            dayjs(event.startDate).format('DD').toString() ===
                              dayjs(event.finishDate).format('DD').toString() &&
                            h.split(':')[0] === dayjs(event.startDate).format('HH').toString() ? (
                              <WeekEvent event={event} key={event.id} />
                            ) : (
                              dayjs(d).format('DD').toString() ===
                                dayjs(event.startDate).format('DD').toString() &&
                              dayjs(event.startDate).format('DD').toString() !==
                                dayjs(event.finishDate).format('DD').toString() &&
                              h.split(':')[0] ===
                                dayjs(event.startDate).format('HH').toString() && (
                                <Box
                                  className={style.eventNotSameDay}
                                  onClick={() => handlerClick(event)}
                                  style={{
                                    backgroundColor: getColorByCategory(event.eventCategory),
                                    color:
                                      getColorByCategory(event.eventCategory) === '#2E7161'
                                        ? 'white'
                                        : 'black',
                                  }}
                                >
                                  <div className={style.iconEventCategoryNotSameDay}>
                                    {getIcon(event.eventCategory)}
                                  </div>
                                  <h3
                                    className={style.eventTitleNotSameDay}
                                    style={{
                                      color:
                                        getColorByCategory(event.eventCategory) === '#2E7161'
                                          ? 'white'
                                          : 'black',
                                    }}
                                  >
                                    {reduceString(event.title, 15)}
                                  </h3>
                                </Box>
                              )
                            )
                          )}
                          <Tooltip title={t('seeMore')}>
                            <Box
                              className={style.pillMultiEvents}
                              onClick={() =>
                                handleOpenModalMultiEvent(dayjs(d).format('DD').toString(), h)
                              }
                              style={{
                                backgroundColor: getColorByCategory(6),
                                color: 'white',
                              }}
                            >
                              <h3 className={style.pillMultiEventsText} style={{ color: 'white' }}>
                                ...
                              </h3>
                            </Box>
                          </Tooltip>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </div>
            <div className={style.buttonAddAppointmentContainer}>
              <Button onClick={createEvent} className={style.buttonAddAppointment}>
                <img src={addIcon} alt="add" className={style.addIcon} />
                {t('addAppointment')}
              </Button>
            </div>
          </Table>
          {selectedEvent && (
            <Modal open={openModal} className={style.eventModal} onClose={handleCloseModal}>
              <CalendarModal
                event={selectedEvent}
                handleClose={handleCloseModal}
                handleEdit={editEvent}
                handleRemove={deleteEvent}
              />
            </Modal>
          )}
          {dateTimeMultiEvents && (
            <Modal
              open={openModalMultiEvent}
              className={style.eventModal}
              onClose={handleCloseModalMultiEvent}
            >
              <CalendarModalMultiEvents
                events={events}
                handleClose={handleCloseModalMultiEvent}
                handlerEventClick={handlerClick}
                setOpenModal={setOpenModal}
                date={dateTimeMultiEvents.date}
                hour={dateTimeMultiEvents.hour}
              />
            </Modal>
          )}
        </div>
      </div>
    </div>
  )
}
